/**
 * resourceAuditResource — the GLOBAL data-change ledger (#1981, epic #1982 A3).
 *
 * `/audit` reads `audit_logs`: one row per privileged HTTP call. This resource
 * reads `resource_audit_logs`: one row per DATA CHANGE — which field of which
 * record became what, and who or which webhook caused it. Two ledgers, two
 * questions, hence two tabs rather than one page with a mode switch.
 *
 * Read-only, like its sibling: no create/edit/delete, no `/:id` route, so the
 * detail surface is the drawer and the form view carries no tabs.
 *
 * ── Why the record is the WIRE row, not the mapped timeline item ────────────
 *
 * Every column key here doubles as a backend key: the list kernel filters on
 * `resource_type` / `action` / `source` / `actor_id` and sorts on `created_at`,
 * and both whitelists (`RESOURCE_AUDIT_FILTERS` / `RESOURCE_AUDIT_SORTS`) are
 * generated from the backend config. A camelCase record would need a `filterId`
 * on every field AND would emit `sort=createdAt:desc`, a key the kernel's sort
 * whitelist does not hold. So the row stays snake_case and `api.list` attaches
 * what the cells additionally need:
 *
 *   `entry`      — the parsed `ResourceAuditTimelineItem` (`changes` is a JSON
 *                  string on the wire; parsing it in a cell would either
 *                  duplicate `mapResourceAuditLog` or import it into a widget
 *                  module, which is forbidden — see `./widgets.tsx`'s header).
 *   `fieldIndex` — the field table of the resource THIS row is about, so the
 *                  diff renders with that resource's own labels and value
 *                  widgets instead of raw wire keys.
 *
 * The four bundle imports below are deliberate and load-bearing: "render a
 * change with the changed resource's own field labels" IS a dependency on those
 * four resources. Copying their labels into audit would create a second truth
 * that has to be kept in sync by hand. Each comes through its feature BARREL —
 * a deep `@features/orders/schema` import is blocked by
 * `__tests__/architecture/feature-barrels`.
 */
import {
  createFieldConstructors,
  defineFormView,
  defineListView,
  defineModel,
  type AnyFieldDef,
  type EnumOption,
} from '@resource-kit/model';
import type { ResourceBundle } from '@resource-kit/controllers/react';
import { viewFieldOf } from '@resource-kit/ui/react';
import type { FieldIndex } from '@components/ui/AuditChangeViews';
import { RESOURCE_AUDIT_FILTERS, RESOURCE_AUDIT_SORTS } from '@services/filters.generated';
import {
  mapResourceAuditLog,
  resourceAuditAPI,
  type ResourceAuditLog,
  type ResourceAuditResourceType,
  type ResourceAuditTimelineItem,
} from '@services/audit';
import { toListResult } from '@services/list-envelope';
import { orderResource } from '@features/orders';
import { customerResource } from '@features/customers';
import { productResource } from '@features/products';
import { jobResource } from '@features/dispatch';

/** A ledger row plus the two projections its cells cannot derive alone. */
export interface ResourceAuditRow extends ResourceAuditLog {
  entry: ResourceAuditTimelineItem;
  fieldIndex: FieldIndex<never>;
}

/**
 * A bundle's declared fields as an index keyed by the column name an audit row
 * names them with.
 *
 * Built from `model.fields`, not from `list.items`: the ledger records every
 * column the store writes, including ones no list view shows, and an unknown
 * key degrades to the raw key (`AuditChangeViews`) — a poorer read than the
 * label that already exists two lines away.
 *
 * `never` for the record type is sound rather than lazy: the index is read for
 * `label` / `type` / `options` / `widget` only, and `AuditChangeViews` states
 * that it deliberately never consults a field's `render` (the one member that
 * would need the real row).
 */
function fieldIndexOf(bundle: {
  model: { fields: Record<string, AnyFieldDef> };
}): FieldIndex<never> {
  return new Map(
    Object.entries(bundle.model.fields).map(([key, modelField]) => [
      key,
      viewFieldOf<never, Record<string, AnyFieldDef>>({ field: key }, modelField),
    ]),
  );
}

/**
 * tenant_settings has no resource bundle. Its ledger rows contain only the
 * five outbound red-line columns; AuditChangeViews renders unknown keys as raw
 * column names, matching the runbook SQL.
 */
const NO_BUNDLE_FIELDS: { model: { fields: Record<string, AnyFieldDef> } } = {
  model: { fields: {} },
};

/**
 * Which bundle describes each audited resource type — behind a thunk, and
 * memoised on first use.
 *
 * Building the four indexes at module scope broke four route suites that mock a
 * feature barrel: the stub has no `model`, and this module is reached from
 * `App.tsx` through the audit barrel, so the import itself threw before any
 * test ran. Nothing needs an index until a ledger row is actually rendered, so
 * the work belongs there.
 */
const BUNDLE_FOR = {
  orders: () => orderResource,
  customers: () => customerResource,
  products: () => productResource,
  fulfillment_jobs: () => jobResource,
  tenant_settings: () => NO_BUNDLE_FIELDS,
} as const satisfies Record<
  ResourceAuditResourceType,
  () => { model: { fields: Record<string, AnyFieldDef> } }
>;

const FIELD_INDEX_CACHE = new Map<ResourceAuditResourceType, FieldIndex<never>>();

function fieldIndexFor(type: ResourceAuditResourceType): FieldIndex<never> {
  const cached = FIELD_INDEX_CACHE.get(type);
  if (cached !== undefined) return cached;
  const index = fieldIndexOf(BUNDLE_FOR[type]());
  FIELD_INDEX_CACHE.set(type, index);
  return index;
}

/**
 * Labels for the five audited resource types. Named here rather than borrowed
 * from each resource's own `host.label` because `CONTEXT.md` — the authority
 * for domain vocabulary — settles the Chinese words (订单 / 客户 / 商品 /
 * 配送单), and one of the existing labels drifted off it.
 */
const RESOURCE_TYPE_OPTIONS: EnumOption[] = [
  { value: 'orders', label: 'audit.resourceType.orders' },
  { value: 'customers', label: 'audit.resourceType.customers' },
  { value: 'products', label: 'audit.resourceType.products' },
  { value: 'fulfillment_jobs', label: 'audit.resourceType.fulfillment_jobs' },
  { value: 'tenant_settings', label: 'audit.resourceType.tenant_settings' },
];

/**
 * The four ledger actions. Labels are the ones the per-record history timeline
 * already uses, so the same event reads identically on both surfaces; `color`
 * mirrors that timeline's badge variants.
 */
const ACTION_OPTIONS: EnumOption[] = [
  { value: 'create', label: 'audit.created', color: 'success' },
  { value: 'update', label: 'audit.updated', color: 'primary' },
  { value: 'delete', label: 'audit.deleted', color: 'destructive' },
  { value: 'restore', label: 'audit.restored', color: 'secondary' },
];

/**
 * The closed source set from the wire schema. Distinct keys from
 * `audit.actor.*`: those phrase an ACTOR ("Shopify · <event>", "Manual (actor
 * unattributed)"), while this column names the CHANNEL a change came in on.
 */
const SOURCE_OPTIONS: EnumOption[] = [
  { value: 'manual', label: 'audit.source.manual' },
  { value: 'shopify_webhook', label: 'audit.source.shopify_webhook', color: 'primary' },
  { value: 'onfleet_webhook', label: 'audit.source.onfleet_webhook', color: 'primary' },
  { value: 'cron', label: 'audit.source.cron', color: 'secondary' },
  { value: 'backfill', label: 'audit.source.backfill', color: 'secondary' },
  { value: 'smoke', label: 'audit.source.smoke', color: 'outline' },
];

const f = createFieldConstructors<ResourceAuditRow>();

/** Attach the two derived projections the cells read. */
function toRow(row: ResourceAuditLog): ResourceAuditRow {
  return {
    ...row,
    entry: mapResourceAuditLog(row),
    fieldIndex: fieldIndexFor(row.resource_type),
  };
}

export const resourceAuditModel = defineModel<ResourceAuditRow>()({
  name: 'resource-audit',
  idField: 'id',
  recName: 'resource_id',
  fields: {
    created_at: f.datetime({
      label: 'audit.colTimestamp',
      // Filterable through the QueryBuilder like every other column, and
      // through NOTHING ELSE. It used to be `filterable: false` behind a
      // bespoke daterange quick filter, which put the field an operator
      // filters by most often in the one place the condition row could
      // not reach. `RESOURCE_AUDIT_FILTERS` declares from/to/between on
      // it and the date operator table offers exactly those three.
      widget: 'auditTimestamp',
      // FORENSIC (#2105): the change ledger is read against Worker logs and
      // webhook receipts, all UTC. Same reasoning as `auditResource` — and it
      // has to be declared here too, because the reading is a property of the
      // COLUMN, and these are two separate surfaces over two separate tables.
      instantReading: 'utc',
    }),
    resource_type: f.enum({
      label: 'audit.colResourceType',
      options: RESOURCE_TYPE_OPTIONS,
    }),
    resource_id: f.id({
      label: 'audit.colResource',
      // Exact-UUID equality is a filter no operator types by hand; the
      // per-record history tab is how you ask "this record's changes".
      filterable: false,
      widget: 'auditResourceLink',
    }),
    action: f.enum({
      label: 'audit.colAction',
      options: ACTION_OPTIONS,
    }),
    source: f.enum({
      label: 'audit.colSource',
      options: SOURCE_OPTIONS,
      widget: 'auditChangeSource',
    }),
    // Display column; the backend filters actors by `actor_id`, so the
    // QueryBuilder entry is pinned there through `filterId` (the same
    // display-key / filter-key split `auditResource.actor_label` makes).
    actor_label: f.text({
      label: 'audit.colActor',
      filterId: 'actor_id',
      widget: 'auditActor',
    }),
    // The raw recorded diff. The widget renders it as a one-line summary in
    // the table and as the full field-by-field diff in the drawer.
    changes: f.text({
      label: 'audit.colChangeSummary',
      filterable: false,
      widget: 'auditChangeSummary',
    }),
  },
});

export const resourceAuditListView = defineListView(resourceAuditModel, {
  items: [
    { field: 'created_at' },
    { field: 'resource_type' },
    { field: 'resource_id' },
    { field: 'action' },
    { field: 'source' },
    { field: 'actor_label' },
    { field: 'changes' },
  ],
  defaultSort: { field: 'created_at', dir: 'desc' },
  // No `capabilities.search`: the ledger declares no searchable column
  // (`RESOURCE_AUDIT_SEARCH` is empty) and the kernel 400s the reserved
  // param. Suppression is a consequence of the non-empty derived filter
  // schema, so the key stays absent rather than claiming a switch.
});

/** Read-only ledger: the drawer shows the same columns; there is no form. */
export const resourceAuditFormView = defineFormView(resourceAuditModel, {
  groupDisplay: 'tab',
  children: [],
});

export const resourceAuditResource: ResourceBundle<
  ResourceAuditRow,
  typeof resourceAuditModel.fields
> = {
  model: resourceAuditModel,
  list: resourceAuditListView,
  form: resourceAuditFormView,
  host: {
    api: {
      // Projects the raw rows AND renames the kernel's `size` to the
      // framework's `limit` (#2167 PR-2). The rename is DELEGATED, not
      // repeated: the spread is over `toListResult`'s return, which is built
      // key by key as `{ items, total, page, limit }`
      // (`src/services/list-envelope.ts::toListResult`), so there is no `size` in it to
      // leak. Only `items` is then replaced, by the projected rows.
      list: async (params) => {
        const page = await resourceAuditAPI.listRaw(params);
        return { ...toListResult(page), items: page.items.map(toRow) };
      },
    },
    // The vocabulary is settled: 变更历史, the same word the per-record tab
    // carries. No new term is coined for the global view of it.
    label: 'resource.changeHistory',
    slug: 'resource-audit',
    listTitle: 'resource.changeHistory',
    // The counterpart of `auditResource`'s line: what THIS ledger records
    // (the data change itself, not the request). It used to live in a
    // legend under the tab strip, which put the copy below the control it
    // explains and left this route's title line bare (owner, 2026-08-08).
    listDescription: 'audit.tab.changes.description',
    filterable: RESOURCE_AUDIT_FILTERS,
    sortable: RESOURCE_AUDIT_SORTS,
    detail: { surface: 'drawer' },
  },
};
